import { registerPlugin } from "@capacitor/core";

export interface Reminder {
  id: string; // task id
  title: string;
  body: string;
  at: number; // epoch ms
  noteId: string;
}

/** Native side of the Android app (apps/android/.../EngramNativePlugin.kt). */
export interface EngramNativePlugin {
  getToken(): Promise<{ token?: string }>;
  /** Also stores the server URL so widgets and notification actions can call the API natively. */
  setToken(o: { token: string | null; apiBase?: string }): Promise<void>;
  /** JSON snapshot the home-screen widgets render (recent notes, shortcuts, open tasks). */
  updateWidgets(o: { snapshot: string }): Promise<void>;
  /** Replaces all scheduled reminder notifications. */
  scheduleReminders(o: { reminders: Reminder[] }): Promise<void>;
  requestNotificationPermission(): Promise<{ granted: boolean }>;

  // App updates (Updater.kt; ./updater.ts). Not in apps built before native version 1: check `native` first.
  updaterInfo(): Promise<UpdaterInfo>;
  /** The app is up: ends a new bundle's trial start (otherwise it rolls back after 30 s). */
  appReady(): Promise<UpdaterInfo>;
  /** Downloads, checks and unpacks a bundle; it then waits as `next`. */
  downloadBundle(o: { id: string; sha256: string }): Promise<UpdaterInfo>;
  /** On: the waiting bundle is used at the next start. Only while nothing waits to sync. */
  armUpdate(o: { on: boolean }): Promise<UpdaterInfo>;
  /** Switches to the waiting bundle now (the page reloads into it). */
  applyUpdateNow(): Promise<{ applied: boolean }>;
  downloadApk(o: { versionCode: number; sha256: string }): Promise<void>;
  /**
   * Opens Android's installer. "permission": installs from Engram aren't allowed yet, so Android's setting opened
   * instead (`ask` false: nothing opens). Allowing it there restarts the app.
   */
  installApk(o: { versionCode: number; ask?: boolean }): Promise<{ status: "started" | "permission" }>;
}

export interface UpdaterInfo {
  /** This app's native version (apps/android/native.json when it was built). */
  native: number;
  versionCode: number;
  versionName: string;
  /** Bundle ids; "" = the one inside the APK. */
  current: string;
  previous: string;
  trying: boolean;
  next: string | null;
  armed: boolean;
  failed: string[];
  /** A bundle whose trial failed, so the app went back to the previous one. */
  rolledBack: string | null;
}

export const EngramNative = registerPlugin<EngramNativePlugin>("EngramNative");
